// Event market list ordering — a preference, so it outlives the current event.

import { useCallback, useState } from 'react';

export type EventMarketSort = 'volume' | 'probability';

export const EVENT_MARKET_SORT_KEY = 'irm.eventMarketSort';

/** Stored preference, defaulting to probability whenever it cannot be read. */
export function readStoredSort(
    storage: Pick<Storage, 'getItem'> | undefined
): EventMarketSort {
    try {
        return storage?.getItem(EVENT_MARKET_SORT_KEY) === 'volume'
            ? 'volume'
            : 'probability';
    } catch {
        return 'probability';
    }
}

/** Persist the preference, ignoring storage failures (private mode, quota). */
export function writeStoredSort(
    storage: Pick<Storage, 'setItem'> | undefined,
    sort: EventMarketSort
): void {
    try {
        storage?.setItem(EVENT_MARKET_SORT_KEY, sort);
    } catch {
        /* preference is best-effort */
    }
}

function browserStorage(): Storage | undefined {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
}

export function useEventMarketSort(): [
    EventMarketSort,
    (sort: EventMarketSort) => void,
] {
    const [sort, setSort] = useState<EventMarketSort>(() =>
        readStoredSort(browserStorage())
    );
    const update = useCallback((next: EventMarketSort) => {
        setSort(next);
        writeStoredSort(browserStorage(), next);
    }, []);
    return [sort, update];
}
